import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Slider } from "@/components/ui/slider";
import {
  Info, Database, RefreshCw, Download, BookOpen, Search, TrendingUp, TrendingDown, Minus,
  AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Settings2, Play
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend,
  Tooltip as RTooltip, ResponsiveContainer, ComposedChart, Line, Cell,
} from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/capacity-planning`;

const Q_COLORS = {
  new_apps: "#502D3C", limit_reviews: "#6366F1", watchlist: "#EAB308", collections: "#DC2626",
  failed_debit: "#EA580C", periodic: "#65A30D", bureau_refresh: "#0891B2", escalated: "#9333EA",
};

const fmt = (v) => { if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`; if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`; return v.toFixed?.(1) ?? v; };

const heatColor = (v, max) => {
  const p = v / Math.max(1, max);
  if (p < 0.2) return "bg-green-50 text-green-700";
  if (p < 0.4) return "bg-emerald-50 text-emerald-700";
  if (p < 0.6) return "bg-amber-50 text-amber-700";
  if (p < 0.8) return "bg-orange-100 text-orange-700";
  return "bg-red-100 text-red-700";
};

export default function CapacityPlanningDashboard() {
  const [data, setData] = useState(null);
  const [glossary, setGlossary] = useState({});
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(7);
  const [unitMode, setUnitMode] = useState("work_units");
  const [isAnalystView, setIsAnalystView] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [showScenario, setShowScenario] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [metricInfo, setMetricInfo] = useState(null);
  const [glossarySearch, setGlossarySearch] = useState("");
  const [activeTab, setActiveTab] = useState("analyst");
  const [tableSortBy, setTableSortBy] = useState(null);
  const [tableSortDir, setTableSortDir] = useState("desc");

  const [scenario, setScenario] = useState({ add_analysts: 0, remove_analysts: 0, utilization_override: null, manual_review_rate_override: null, fail_rate_multiplier: 1.0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [d, g] = await Promise.all([axios.get(`${API}/dashboard?horizon=${horizon}`), axios.get(`${API}/glossary`)]);
      setData(d.data); setGlossary(g.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [horizon]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runScenario = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scenario`, { ...scenario, horizon });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openMetric = async (key) => {
    setSelectedMetric(key);
    try { const r = await axios.get(`${API}/metrics/${key}`); setMetricInfo(r.data); }
    catch { setMetricInfo({ title: key, definition: "Info not available." }); }
  };

  const exportCSV = (rows, filename) => {
    if (!rows?.length) return;
    const headers = Object.keys(rows[0]).filter(k => typeof rows[0][k] !== 'object');
    const csv = [headers.join(","), ...rows.map(r => headers.map(h => `"${r[h] ?? ""}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = filename; a.click();
  };

  const s = data?.summary;
  const b = data?.briefing;
  const isWU = unitMode === "work_units";

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="h-8 w-8 animate-spin text-[#502D3C]" /></div>;

  // Build demand mix chart data
  const demandStack = data?.demand_mix ? [
    { period: "Next 7d", ...Object.fromEntries(data.demand_mix.map(q => [q.queue_id, isWU ? q.units_7d : q.cases_7d])) },
    { period: "Next 30d", ...Object.fromEntries(data.demand_mix.map(q => [q.queue_id, isWU ? q.units_30d : q.cases_30d])) },
  ] : [];

  // Backlog chart data
  const backlogChart = data?.backlog?.map(b => ({
    name: b.queue_name.replace(/ /g, '\n').split('\n')[0], ...b.aging, total: b.open_cases
  })) || [];

  // Max heatmap value for color scaling
  const hmMax = data?.heatmap ? Math.max(...data.heatmap.flatMap(r => ["daily", "weekly", "bi_weekly", "monthly"].map(t => r[t]?.work_units || 0))) : 1;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="capacity-planning-page">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-xl font-semibold text-[#502D3C] tracking-tight" data-testid="page-title">Capacity Planning, Allocation & Forecasting</h1>
              <p className="text-[10px] text-[#64748B]">Operational workload view for Rippling's non-revolving card business</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-gray-100 rounded p-0.5">
                <button onClick={() => setHorizon(7)} className={cn("px-2.5 py-1 text-[10px] rounded font-medium transition-colors", horizon === 7 ? "bg-white text-[#502D3C] shadow-sm" : "text-[#94A3B8]")} data-testid="horizon-7d">7 Days</button>
                <button onClick={() => setHorizon(30)} className={cn("px-2.5 py-1 text-[10px] rounded font-medium transition-colors", horizon === 30 ? "bg-white text-[#502D3C] shadow-sm" : "text-[#94A3B8]")} data-testid="horizon-30d">30 Days</button>
              </div>
              <div className="flex bg-gray-100 rounded p-0.5">
                <button onClick={() => setUnitMode("cases")} className={cn("px-2.5 py-1 text-[10px] rounded font-medium transition-colors", !isWU ? "bg-white text-[#502D3C] shadow-sm" : "text-[#94A3B8]")}>Cases</button>
                <button onClick={() => setUnitMode("work_units")} className={cn("px-2.5 py-1 text-[10px] rounded font-medium transition-colors", isWU ? "bg-white text-[#502D3C] shadow-sm" : "text-[#94A3B8]")}>Work Units</button>
              </div>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowScenario(true)} data-testid="scenario-btn"><Settings2 className="h-3.5 w-3.5 mr-1" />Scenario</Button>
              <Button variant="ghost" size="sm" onClick={() => setShowGlossary(true)} className="h-8 text-xs text-[#64748B]"><BookOpen className="h-3.5 w-3.5 mr-1" />Glossary</Button>
              <Button variant="ghost" size="icon" onClick={fetchData} className="h-8 w-8"><RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /></Button>
              <div className="ml-1 flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded text-[11px]">
                <span className={!isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Simple</span>
                <Switch checked={isAnalystView} onCheckedChange={setIsAnalystView} className="scale-75" data-testid="view-toggle" />
                <span className={isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Analyst</span>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 space-y-4">
          {/* ROW 0: Executive Banner */}
          {b && (
            <Card className="bg-[#502D3C] text-white border-0" data-testid="briefing-banner">
              <CardContent className="p-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-5 space-y-1.5">
                    <Badge className="bg-[#FFCC1C] text-[#502D3C] text-[9px] h-4 font-semibold">Capacity Briefing</Badge>
                    {b.takeaways.map((t, i) => <p key={i} className="text-xs text-white/90 leading-relaxed flex items-start gap-1.5"><span className="text-[#FFCC1C] text-[10px] mt-0.5 font-bold">{i + 1}.</span>{t}</p>)}
                  </div>
                  <div className="col-span-4 border-l border-white/10 pl-4">
                    <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="h-3 w-3 text-red-400" /><span className="text-[9px] font-semibold text-red-300 uppercase tracking-wider">Risk Alert</span></div>
                    <p className="text-xs text-white/85 leading-relaxed">{b.risk_alert}</p>
                  </div>
                  <div className="col-span-3 border-l border-white/10 pl-4">
                    <div className="flex items-center gap-1.5 mb-1"><span className="w-4 h-4 bg-[#FFCC1C] rounded flex items-center justify-center text-[8px] font-bold text-[#502D3C]">!</span><span className="text-[9px] font-semibold text-[#FFCC1C] uppercase tracking-wider">Next Step</span></div>
                    <p className="text-xs text-white/85 leading-relaxed">{b.recommendation}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-2 border-t border-white/10">
                  <span className="text-[9px] text-white/50">{b.data_freshness}</span>
                  <Badge variant="outline" className="text-[9px] h-4 border-white/20 text-white/60">Confidence: {b.forecast_confidence}</Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ROW 1: Summary Cards */}
          {s && (
            <div className="grid grid-cols-6 gap-3" data-testid="summary-cards">
              <SCard title="Forecasted Workload" value={isWU ? `${s.forecasted_workload_7d.toFixed(0)} units` : `${s.forecasted_cases_7d} cases`} delta={s.forecasted_workload_7d_delta} interp={`Expected incoming work (${horizon}d)`} onInfo={() => openMetric("forecasted_workload_7d")} src="ops.forecast_output_daily" ts={s.last_refreshed} status={s.buffer_pct < 5 ? "risk" : s.buffer_pct < 15 ? "watch" : "good"} analyst={isAnalystView} />
              <SCard title="Available Capacity" value={`${s.available_capacity_7d.toFixed(0)} units`} delta={s.available_capacity_7d_delta} interp="Total team capacity for the period" onInfo={() => openMetric("available_capacity_7d")} src="ops.analyst_capacity_daily" ts={s.last_refreshed} status="good" analyst={isAnalystView} invert />
              <SCard title="Buffer / Shortfall" value={`${s.buffer_pct > 0 ? '+' : ''}${s.buffer_pct.toFixed(1)}%`} delta={s.buffer_pct_delta} interp={s.buffer_pct > 0 ? "Capacity exceeds demand — buffer exists" : "Demand exceeds capacity — shortfall"} onInfo={() => openMetric("buffer_pct")} src="Calculated" ts={s.last_refreshed} status={s.buffer_pct < 0 ? "risk" : s.buffer_pct < 10 ? "watch" : "good"} analyst={isAnalystView} invert />
              <SCard title="SLA At-Risk Cases" value={s.sla_at_risk} delta={s.sla_at_risk_delta} interp="Cases likely to miss SLA target" onInfo={() => openMetric("sla_at_risk")} src="ops.forecast_output_daily" ts={s.last_refreshed} status={s.sla_at_risk > 10 ? "risk" : s.sla_at_risk > 3 ? "watch" : "good"} analyst={isAnalystView} />
              <SCard title="Collections Due" value={s.collections_due} delta={s.collections_due_delta} interp="Manual follow-ups expected" onInfo={() => openMetric("collections_due")} src="ops.forecast_driver_daily" ts={s.last_refreshed} status={s.collections_due > 100 ? "risk" : s.collections_due > 60 ? "watch" : "good"} analyst={isAnalystView} />
              <SCard title="Manual Review Rate" value={`${s.manual_review_rate.toFixed(1)}%`} delta={s.manual_review_rate_delta} interp="Share requiring human review" onInfo={() => openMetric("manual_review_rate")} src="ops.forecast_output_daily" ts={s.last_refreshed} status={s.manual_review_rate > 45 ? "risk" : s.manual_review_rate > 35 ? "watch" : "good"} analyst={isAnalystView} />
            </div>
          )}

          {/* ROW 2: Demand Mix + Heatmap */}
          <div className="grid grid-cols-2 gap-4">
            <Card data-testid="demand-mix-chart">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Demand Mix by Workflow</CardTitle>
                <p className="text-[10px] text-[#64748B]">Where incoming workload comes from ({isWU ? "work units" : "cases"})</p></CardHeader>
              <CardContent className="px-4 pb-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={demandStack} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 9 }} />
                    <RTooltip contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    {data?.demand_mix?.map(q => <Bar key={q.queue_id} dataKey={q.queue_id} stackId="a" fill={Q_COLORS[q.queue_id]} name={q.queue_name} />)}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="heatmap">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Due-Cycle Workload Heatmap</CardTitle>
                <p className="text-[10px] text-[#64748B]">When scheduled repayments create manual work (work units)</p></CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px]">
                    <thead><tr className="border-b border-gray-100">
                      <th className="text-left py-1 text-[9px] text-[#64748B] font-medium w-16">Date</th>
                      <th className="text-center py-1 text-[9px] text-[#64748B] font-medium">Daily</th>
                      <th className="text-center py-1 text-[9px] text-[#64748B] font-medium">Weekly</th>
                      <th className="text-center py-1 text-[9px] text-[#64748B] font-medium">Bi-Weekly</th>
                      <th className="text-center py-1 text-[9px] text-[#64748B] font-medium">Monthly</th>
                    </tr></thead>
                    <tbody>
                      {data?.heatmap?.map((row, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-1 text-[#502D3C] font-medium">{row.date} <span className="text-[#94A3B8]">{row.dow}</span></td>
                          {["daily", "weekly", "bi_weekly", "monthly"].map(t => (
                            <td key={t} className="py-1 text-center">
                              <span className={cn("inline-block px-2 py-0.5 rounded text-[9px] font-medium min-w-[36px]", heatColor(row[t]?.work_units || 0, hmMax))}>
                                {(row[t]?.work_units || 0).toFixed(1)}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ROW 3: Backlog + Analyst Utilization */}
          <div className="grid grid-cols-2 gap-4">
            <Card data-testid="backlog-chart">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Backlog & Aging by Queue</CardTitle>
                <p className="text-[10px] text-[#64748B]">Open cases by queue and age bucket</p></CardHeader>
              <CardContent className="px-4 pb-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={backlogChart} layout="vertical" margin={{ left: 60, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 9 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={60} />
                    <RTooltip contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    <Bar dataKey="0-24h" stackId="a" fill="#16A34A" name="0-24h" />
                    <Bar dataKey="24-48h" stackId="a" fill="#EAB308" name="24-48h" />
                    <Bar dataKey="2-5d" stackId="a" fill="#EA580C" name="2-5d" />
                    <Bar dataKey="5+d" stackId="a" fill="#DC2626" name="5+d" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="analyst-util-chart">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Analyst Allocation / Utilization</CardTitle>
                <p className="text-[10px] text-[#64748B]">Assigned vs available capacity per analyst</p></CardHeader>
              <CardContent className="px-4 pb-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.analyst_util || []} layout="vertical" margin={{ left: 70, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 9 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={70} />
                    <RTooltip contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    <Bar dataKey="capacity" fill="#E2E8F0" name="Capacity" />
                    <Bar dataKey="assigned" name="Assigned">
                      {(data?.analyst_util || []).map((a, i) => <Cell key={i} fill={a.utilization > 100 ? "#DC2626" : a.utilization > 85 ? "#EA580C" : "#502D3C"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* ROW 4: Forecast vs Capacity + Queue Stress */}
          <div className="grid grid-cols-2 gap-4">
            <Card data-testid="forecast-capacity-chart">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Forecast vs Capacity ({horizon}d)</CardTitle>
                <p className="text-[10px] text-[#64748B]">Projected workload against team capacity with backlog trend</p></CardHeader>
              <CardContent className="px-4 pb-4 h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data?.forecast_capacity || []} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 9 }} />
                    <RTooltip contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 10 }} />
                    <Legend wrapperStyle={{ fontSize: 9 }} />
                    <Area type="monotone" dataKey="forecasted" fill="#502D3C20" stroke="#502D3C" strokeWidth={2} name="Forecast" />
                    <Line type="monotone" dataKey="capacity" stroke="#FFCC1C" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Capacity" />
                    <Line type="monotone" dataKey="backlog" stroke="#DC2626" strokeWidth={1.5} dot={false} name="Backlog" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="queue-stress">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Queue Stress / Hotspots</CardTitle>
                <p className="text-[10px] text-[#64748B]">Most stressed queues and recommended reallocation</p></CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {data?.queue_stress?.sort((a, b) => b.stress - a.stress).map(q => (
                    <div key={q.queue_id} className={cn("p-2 rounded border", q.stress > 70 ? "bg-red-50 border-red-100" : q.stress > 40 ? "bg-amber-50 border-amber-100" : "bg-gray-50 border-gray-100")}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[11px] font-medium text-[#502D3C]">{q.queue_name}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={cn("text-[9px] h-4", q.stress > 70 ? "text-red-600 border-red-200" : q.stress > 40 ? "text-amber-600 border-amber-200" : "text-green-600 border-green-200")}>
                            {q.utilization.toFixed(0)}% util
                          </Badge>
                          <span className="text-[9px] text-[#94A3B8]">{q.analysts} analysts</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                        <span>Forecast: {q.forecast_units.toFixed(0)} units · Capacity: {q.capacity.toFixed(0)} units</span>
                        {q.at_risk > 0 && <span className="text-red-600 font-medium">{q.at_risk} at-risk</span>}
                      </div>
                      {q.recommendation && <p className="text-[10px] text-[#502D3C] mt-1 font-medium">{q.recommendation}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ROW 5: Tabbed Detail Tables */}
          <Card data-testid="detail-tables">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <TabsList className="bg-gray-100 h-8">
                  <TabsTrigger value="analyst" className="text-[11px] h-6 px-3">Analyst View</TabsTrigger>
                  <TabsTrigger value="queue" className="text-[11px] h-6 px-3">Queue View</TabsTrigger>
                  <TabsTrigger value="daily" className="text-[11px] h-6 px-3">Day-by-Day</TabsTrigger>
                </TabsList>
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => exportCSV(
                  activeTab === "analyst" ? data?.analyst_table : activeTab === "queue" ? data?.queue_table : data?.daily_table?.slice(0, 100),
                  `${activeTab}_view.csv`)}><Download className="h-3 w-3 mr-1" />Export</Button>
              </div>

              <TabsContent value="analyst" className="px-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="border-b border-gray-200">
                      {["Analyst", "Team", "Role", "Queue", "Avail Hours", "Util %", "Capacity", "Assigned", "Open Cases", "SLA %", "PTO", "Realloc"].map(h => (
                        <TableHead key={h} className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {data?.analyst_table?.map(a => (
                        <TableRow key={a.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <TableCell className="text-xs font-medium text-[#502D3C]">{a.name}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{a.team}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{a.role}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{a.queue_name}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{a.available_hours}h</TableCell>
                          <TableCell><span className={cn("text-[11px] font-medium", a.utilization_pct > 100 ? "text-red-600" : a.utilization_pct > 90 ? "text-amber-600" : "text-green-600")}>{a.utilization_pct}%</span></TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{a.capacity_units}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{a.assigned_units}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{a.open_cases}</TableCell>
                          <TableCell><span className={cn("text-[11px] font-medium", a.sla_compliance >= 90 ? "text-green-600" : a.sla_compliance >= 75 ? "text-amber-600" : "text-red-600")}>{a.sla_compliance}%</span></TableCell>
                          <TableCell>{a.pto_flag ? <Badge variant="outline" className="text-[9px] h-4 bg-amber-50 text-amber-600 border-amber-200">{a.pto_days}d</Badge> : "—"}</TableCell>
                          <TableCell>{a.reallocation_flag ? <Badge variant="outline" className="text-[9px] h-4 bg-red-50 text-red-600 border-red-200">Flag</Badge> : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="queue" className="px-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="border-b border-gray-200">
                      {["Queue", "Open", "Backlog", "Forecast 7d", "Forecast 30d", "Work Units", "SLA", "At-Risk", "Avg Handle", "Analysts", "Stress", "Action"].map(h => (
                        <TableHead key={h} className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {data?.queue_table?.map(q => (
                        <TableRow key={q.queue_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <TableCell className="text-xs font-medium text-[#502D3C]">{q.queue_name}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{q.open_cases}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{q.backlog_units}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{q.forecast_7d}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{q.forecast_30d}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C] font-medium">{q.forecast_units}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{q.sla_target}</TableCell>
                          <TableCell><span className={cn("text-[11px] font-medium", q.at_risk > 0 ? "text-red-600" : "text-green-600")}>{q.at_risk}</span></TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{q.avg_handle}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{q.analysts}</TableCell>
                          <TableCell><StressBadge stress={q.stress} /></TableCell>
                          <TableCell className="text-[10px] text-[#502D3C] max-w-[150px] truncate">{q.recommendation || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="daily" className="px-0">
                <div className="overflow-x-auto max-h-[400px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10"><TableRow className="border-b border-gray-200">
                      {["Date", "Day", "Queue", "Cases", "Work Units", "Capacity", "Buffer", "Peak Driver", "Rec"].map(h => (
                        <TableHead key={h} className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</TableHead>
                      ))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {data?.daily_table?.slice(0, 80).map((r, i) => (
                        <TableRow key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <TableCell className="text-[11px] text-[#502D3C] font-medium">{r.date}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{r.dow}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{r.queue_name}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C]">{r.cases}</TableCell>
                          <TableCell className="text-[11px] text-[#502D3C] font-medium">{r.units}</TableCell>
                          <TableCell className="text-[11px] text-[#64748B]">{r.capacity}</TableCell>
                          <TableCell><span className={cn("text-[11px] font-medium", r.buffer < 0 ? "text-red-600" : r.buffer < 1 ? "text-amber-600" : "text-green-600")}>{r.buffer > 0 ? "+" : ""}{r.buffer}</span></TableCell>
                          <TableCell className="text-[10px] text-[#64748B]">{r.driver}</TableCell>
                          <TableCell>{r.rec ? <Badge variant="outline" className="text-[9px] h-4 bg-red-50 text-red-600 border-red-200">{r.rec}</Badge> : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </main>

        {/* Scenario Planner Sheet */}
        <Sheet open={showScenario} onOpenChange={setShowScenario}>
          <SheetContent className="w-[420px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">Scenario Planner</SheetTitle></SheetHeader>
            <p className="text-xs text-[#64748B] mt-1 mb-4">Adjust parameters and recompute capacity projections.</p>
            <div className="space-y-5">
              <ScenarioField label="Add Analysts" value={scenario.add_analysts} onChange={v => setScenario(s => ({ ...s, add_analysts: v }))} type="number" min={0} max={10} />
              <ScenarioField label="Remove Analysts" value={scenario.remove_analysts} onChange={v => setScenario(s => ({ ...s, remove_analysts: v }))} type="number" min={0} max={8} />
              <div>
                <label className="text-xs font-medium text-[#502D3C]">Failed Debit Rate Multiplier: {scenario.fail_rate_multiplier.toFixed(1)}x</label>
                <Slider value={[scenario.fail_rate_multiplier * 100]} onValueChange={([v]) => setScenario(s => ({ ...s, fail_rate_multiplier: v / 100 }))} min={50} max={200} step={10} className="mt-2" />
                <div className="flex justify-between text-[9px] text-[#94A3B8] mt-1"><span>0.5x (optimistic)</span><span>2.0x (stress)</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#502D3C]">Manual Review Rate Override</label>
                <Slider value={[scenario.manual_review_rate_override !== null ? scenario.manual_review_rate_override * 100 : 35]} onValueChange={([v]) => setScenario(s => ({ ...s, manual_review_rate_override: v / 100 }))} min={10} max={80} step={5} className="mt-2" />
                <div className="flex justify-between text-[9px] text-[#94A3B8] mt-1"><span>10% (high automation)</span><span>80% (mostly manual)</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#502D3C]">Utilization Override</label>
                <Slider value={[scenario.utilization_override !== null ? scenario.utilization_override * 100 : 78]} onValueChange={([v]) => setScenario(s => ({ ...s, utilization_override: v / 100 }))} min={50} max={95} step={5} className="mt-2" />
                <div className="flex justify-between text-[9px] text-[#94A3B8] mt-1"><span>50%</span><span>95%</span></div>
              </div>
              <Separator />
              <div className="flex gap-2">
                <Button className="flex-1 bg-[#502D3C] hover:bg-[#3d2230]" onClick={() => { runScenario(); setShowScenario(false); }} data-testid="run-scenario-btn"><Play className="h-3.5 w-3.5 mr-1" />Run Scenario</Button>
                <Button variant="outline" onClick={() => { setScenario({ add_analysts: 0, remove_analysts: 0, utilization_override: null, manual_review_rate_override: null, fail_rate_multiplier: 1.0 }); fetchData(); setShowScenario(false); }}>Reset</Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Glossary Sheet */}
        <Sheet open={showGlossary} onOpenChange={setShowGlossary}>
          <SheetContent className="w-[400px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">Glossary</SheetTitle></SheetHeader>
            <Input placeholder="Search terms..." value={glossarySearch} onChange={e => setGlossarySearch(e.target.value)} className="mt-4 mb-3 text-xs" />
            <ScrollArea className="h-[calc(100vh-160px)]">
              <div className="space-y-3 pr-3">
                {Object.entries(glossary).filter(([t]) => t.toLowerCase().includes(glossarySearch.toLowerCase())).map(([term, def]) => (
                  <div key={term} className="p-3 bg-gray-50 rounded">
                    <h4 className="text-xs font-semibold text-[#502D3C] capitalize">{term.replace(/_/g, ' ')}</h4>
                    <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">{def}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Metric Info Sheet */}
        <Sheet open={!!selectedMetric} onOpenChange={() => setSelectedMetric(null)}>
          <SheetContent className="w-[400px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">{metricInfo?.title || ""}</SheetTitle></SheetHeader>
            {metricInfo && (
              <div className="space-y-4 mt-4">
                <div><h4 className="text-xs font-semibold text-[#502D3C] mb-1">What is this?</h4><p className="text-xs text-[#64748B] leading-relaxed">{metricInfo.definition}</p></div>
                {metricInfo.formula && <div><h4 className="text-xs font-semibold text-[#502D3C] mb-1">Formula</h4><p className="text-xs font-mono bg-gray-50 p-2 rounded border">{metricInfo.formula}</p></div>}
                {metricInfo.interpretation && <div><h4 className="text-xs font-semibold text-[#502D3C] mb-1">Interpretation</h4><p className="text-xs text-[#64748B]">{metricInfo.interpretation}</p></div>}
                <Separator />
                <div className="bg-gray-50 p-3 rounded space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-[#64748B]">Source</span><span className="font-mono text-[10px] text-[#502D3C]">{metricInfo.source}</span></div>
                  <div className="flex justify-between"><span className="text-[#64748B]">Refresh</span><span className="text-[#502D3C]">{metricInfo.refresh}</span></div>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}

// ===== HELPERS =====

function SCard({ title, value, delta, interp, onInfo, src, ts, status, analyst, invert }) {
  const dc = delta === null || delta === undefined ? "" : (invert ? (delta < 0 ? "text-red-600" : "text-green-600") : (delta > 0 ? "text-red-600" : "text-green-600"));
  const sc = status === "risk" ? "bg-red-500" : status === "watch" ? "bg-amber-500" : "bg-green-500";
  return (
    <Card className="bg-white border-gray-100 hover:shadow-sm transition-shadow" data-testid={`summary-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5"><div className={cn("w-1.5 h-1.5 rounded-full", sc)} /><p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider leading-tight">{title}</p></div>
          <div className="flex items-center gap-0.5">
            <Tooltip><TooltipTrigger asChild><button onClick={onInfo} className="p-0.5 hover:bg-gray-100 rounded opacity-60 hover:opacity-100"><Info className="h-3 w-3 text-[#94A3B8]" /></button></TooltipTrigger><TooltipContent><p className="text-[10px]">View details</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><button className="p-0.5 opacity-50 hover:opacity-100"><Database className="h-2.5 w-2.5 text-[#94A3B8]" /></button></TooltipTrigger><TooltipContent><p className="text-[10px] font-mono">{src}</p><p className="text-[9px] text-gray-400">{ts}</p></TooltipContent></Tooltip>
          </div>
        </div>
        <p className="text-xl font-bold text-[#502D3C] tracking-tight">{value}</p>
        {delta !== null && delta !== undefined && (
          <span className={cn("flex items-center gap-0.5 text-[10px] font-medium mt-0.5", dc)}>
            {delta > 0 ? <TrendingUp className="h-2.5 w-2.5" /> : delta < 0 ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
            {delta > 0 ? "+" : ""}{typeof delta === 'number' ? delta.toFixed(1) : delta} vs prior
          </span>
        )}
        <p className="text-[9px] text-[#94A3B8] mt-1 border-t border-gray-50 pt-1">{interp}</p>
        {analyst && <p className="text-[8px] text-[#94A3B8] mt-0.5 font-mono">{src}</p>}
      </CardContent>
    </Card>
  );
}

function StressBadge({ stress }) {
  const c = stress > 70 ? "bg-red-100 text-red-600 border-red-200" : stress > 40 ? "bg-amber-100 text-amber-600 border-amber-200" : "bg-green-100 text-green-600 border-green-200";
  return <Badge variant="outline" className={cn("text-[9px] h-4", c)}>{stress > 70 ? "High" : stress > 40 ? "Medium" : "Low"}</Badge>;
}

function ScenarioField({ label, value, onChange, type, min, max }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#502D3C]">{label}</label>
      <Input type={type} value={value} onChange={e => onChange(type === "number" ? parseInt(e.target.value) || 0 : e.target.value)}
        min={min} max={max} className="mt-1 h-8 text-xs" />
    </div>
  );
}
